<template>
  <component :is="as" :class="classes">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cn } from '@/lib/cn';
import type { GridCols } from '@/types/component-types';

export type { GridCols } from '@/types/component-types';

interface Props {
  cols?: GridCols;
  gap?: 2 | 3 | 4 | 6 | 8 | 10 | 12;
  as?: 'div' | 'ul' | 'ol';
}

const props = withDefaults(defineProps<Props>(), {
  cols: () => ({ base: 1, md: 2, lg: 3 }),
  gap: 6,
  as: 'div',
});

const COL_MAP: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  12: 'grid-cols-12',
};

const SM_MAP: Record<number, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
  6: 'sm:grid-cols-6',
  12: 'sm:grid-cols-12',
};

const MD_MAP: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  6: 'md:grid-cols-6',
  12: 'md:grid-cols-12',
};

const LG_MAP: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
  12: 'lg:grid-cols-12',
};

const XL_MAP: Record<number, string> = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  6: 'xl:grid-cols-6',
  12: 'xl:grid-cols-12',
};

const GAP_MAP: Record<number, string> = {
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
};

const classes = computed(() => {
  const cols = props.cols ?? {};
  return cn(
    'grid',
    GAP_MAP[props.gap],
    cols.base && (COL_MAP[cols.base] ?? 'grid-cols-1'),
    cols.sm && SM_MAP[cols.sm],
    cols.md && MD_MAP[cols.md],
    cols.lg && LG_MAP[cols.lg],
    cols.xl && XL_MAP[cols.xl],
  );
});
</script>
